import { collection, addDoc, query, where, getDocs, orderBy, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";

const PUZZLES_COLLECTION = "puzzles";

export interface MoveStep {
  pieceType: string;
  pieceIsLight: boolean;
  from: { row: number; col: number };
  to: { row: number; col: number };
}

export interface MoveRound {
  playerMove: MoveStep;
  alternativePlayerMoves?: MoveStep[]; // additional correct moves for this round
  opponentResponse?: MoveStep; // absent for the final round
}

export interface PuzzleData {
  name: string;
  type: 'bestMove' | 'kingGuesser';
  fen: string;
  playerIsLight: boolean;
  lightKing: string;
  darkKing: string;
  // Best Move — multi-move sequence
  totalMoves?: number;
  moveSequence?: MoveRound[];
  // Legacy single-move fields (backward compat)
  movePieceType?: string;
  movePieceIsLight?: boolean;
  moveFrom?: { row: number; col: number };
  moveTo?: { row: number; col: number };
  createdAt: number;
}

export type SavedPuzzle = PuzzleData & { id: string };

export async function savePuzzle(data: PuzzleData): Promise<string> {
  const docRef = await addDoc(collection(db, PUZZLES_COLLECTION), data);
  return docRef.id;
}

export async function updatePuzzle(id: string, data: Partial<PuzzleData>): Promise<void> {
  const docRef = doc(db, PUZZLES_COLLECTION, id);
  await updateDoc(docRef, data);
}

export async function deletePuzzle(id: string): Promise<void> {
  const docRef = doc(db, PUZZLES_COLLECTION, id);
  await deleteDoc(docRef);
}

export async function getRandomUnseenPuzzle(
  type: 'bestMove' | 'kingGuesser',
  completedIds: string[],
  currentPuzzleId?: string
): Promise<SavedPuzzle | null> {
  const all = await getPuzzlesByType(type);
  if (all.length === 0) return null;
  // Prefer unseen puzzles, but fall back to all if only 1 or fewer remain
  const unseen = all.filter(p => !completedIds.includes(p.id));
  const pool = unseen.length > 1 ? unseen : all;
  // Avoid showing the same puzzle twice in a row
  const filtered = pool.length > 1 && currentPuzzleId
    ? pool.filter(p => p.id !== currentPuzzleId)
    : pool;
  return filtered[Math.floor(Math.random() * filtered.length)];
}

export async function getPuzzlesByType(type: 'bestMove' | 'kingGuesser'): Promise<SavedPuzzle[]> {
  const q = query(
    collection(db, PUZZLES_COLLECTION),
    where("type", "==", type)
  );
  const snapshot = await getDocs(q);
  const puzzles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedPuzzle));
  // Sort client-side to avoid needing a Firestore composite index
  return puzzles.sort((a, b) => b.createdAt - a.createdAt);
}
